/**
 * ZohoCustomerLinkDialog — shared Zoho customer search-and-pick dialog.
 *
 * Feb 2026 — used by both BuyerManagementTab (row action) and
 * CustomerDetail (inline banner action). Reuses the exact
 * `GET /api/zoho/customers/search?q=<name>` endpoint that Dispatch,
 * DeliveryChallan, IBT, and Settings already drive (debounced,
 * >=2 chars, 300ms). On pick, PUTs `customer_zoho_id` to the buyer
 * via the existing `PUT /api/buyers/{buyer_id}` update path — no new
 * persistence mechanism.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export function ZohoCustomerLinkDialog({
  open, onOpenChange, buyer, onLinked, testIdPrefix = "buyer-zoho-link",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Prefill with the buyer's own name — matches how ops naturally
  // start a search when trying to link an existing record.
  useEffect(() => {
    if (open) {
      setQuery(buyer?.name || "");
      setResults([]);
    }
  }, [open, buyer?.name]);

  // Debounced Zoho search — same shape as Dispatch/DC/IBT (300ms, >=2 chars).
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(q)}`,
          auth(),
        );
        if (!cancelled) setResults(res.data || []);
      } catch {
        if (!cancelled) {
          toast.error("Failed to search Zoho customers");
          setResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, open]);

  const link = async (contact) => {
    if (!buyer?.id) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/buyers/${buyer.id}`,
        { customer_zoho_id: contact.contact_id },
        auth(),
      );
      toast.success(`Linked to ${contact.contact_name}`);
      onOpenChange(false);
      // Parent decides how to refresh — row-level fetch or state patch.
      onLinked?.(contact);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link buyer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Link "{buyer?.name}" to Zoho Customer</DialogTitle>
          <DialogDescription>
            Search Zoho by customer name or GSTIN. Pick the correct
            match to link this buyer. Dispatches for this buyer are
            blocked until it's linked.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type at least 2 characters (name or GSTIN)…"
            className="pl-9"
            data-testid={`${testIdPrefix}-search-input`}
          />
        </div>

        <div className="min-h-[240px] max-h-[400px] overflow-y-auto border rounded-md">
          {searching && (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid={`${testIdPrefix}-searching`}>
              Searching Zoho…
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid={`${testIdPrefix}-no-results`}>
              No Zoho customers matched. If none of the results look right,
              contact an admin to have this buyer linked or created in Zoho.
            </div>
          )}
          {!searching && query.trim().length < 2 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search.
            </div>
          )}
          {results.map((c) => (
            <div
              key={c.contact_id}
              className="flex items-center justify-between p-3 border-b hover:bg-slate-50"
              data-testid={`${testIdPrefix}-result-${c.contact_id}`}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">{c.contact_name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {c.company_name && <span>{c.company_name} · </span>}
                  {c.gst_no ? <span className="font-mono">GSTIN {c.gst_no}</span> : <span>No GSTIN on file</span>}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => link(c)}
                disabled={saving}
                data-testid={`${testIdPrefix}-pick-${c.contact_id}`}
              >
                <Link2 className="w-3.5 h-3.5 mr-1" /> Link
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ZohoCustomerLinkDialog;
